import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Card } from '../components/Card'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { Icon } from '../components/Icon'
import { fmtMoney, FMT_INT } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { useTranslation } from 'react-i18next'

interface OperatingCash {
  period: { from: string; to: string; label: string }
  operating_cash_cents: number
  gross_in_cents: number
  gross_out_cents: number
  transactions: number
  prior_period_cents: number
  delta_pct: number | null
}

interface CashKPIs {
  operating_ytd_cents: number
  deposit_churn_ytd_cents: number
  carrier_30d_cents: number
  carrier_30d_split: { usps_pct: number; dhl_pct: number } | null
  latest_balance_cents: number | null
  latest_balance_date: string | null
}

interface ShipKPIs {
  ships: number
  velocity_per_day: number
}

interface CustomerKPIs {
  active_customers: number
  deposit_float_cents: number | null
}

interface PulseAction {
  type: string
  label: string
  count: number
  amount_cents?: number
  href: string
  priority: 'high' | 'medium' | 'low'
}

export function PulseView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const navigate = useNavigate()

  const operating = useQuery<OperatingCash>({
    queryKey: ['cash', 'operating', 'this_month'],
    queryFn: () => fetch('/api/cash/operating?period=this_month').then(r => r.json()),
  })
  const cashKpis = useQuery<CashKPIs>({
    queryKey: ['cash', 'kpis'],
    queryFn: () => fetch('/api/cash/kpis').then(r => r.json()),
  })
  const shipKpis = useQuery<ShipKPIs>({
    queryKey: ['ships', 'kpis', 'today'],
    queryFn: () => fetch('/api/shipments/kpis?period=today').then(r => r.json()),
  })
  const custKpis = useQuery<CustomerKPIs>({
    queryKey: ['customers', 'kpis'],
    queryFn: () => fetch('/api/customers/kpis').then(r => r.json()),
  })
  const pulseActions = useQuery<{ actions: PulseAction[] }>({
    queryKey: ['pulse', 'actions'],
    queryFn: () => fetch('/api/pulse/actions').then(r => r.json()),
    staleTime: 2 * 60_000,
  })

  const op = operating.data
  const ck = cashKpis.data
  const sk = shipKpis.data
  const ek = custKpis.data

  // Days of runway: liquid ÷ daily operating burn (only if burn is negative)
  const monthlyOperating = op?.operating_cash_cents ?? 0
  const daysOfRunway = (ck?.latest_balance_cents && monthlyOperating < 0)
    ? Math.round(ck.latest_balance_cents / (Math.abs(monthlyOperating) / 30))
    : null

  return (
    <div className="space-y-5">
      {/* Hero */}
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">
              {t('pulse.operating_cash_this_month')}
              <InfoIcon tip={t('pulse.operating_cash_tip')} />
            </div>
            <div className="mt-2 text-[44px] font-bold text-ink-900 leading-none">
              {operating.isLoading
                ? <span className="text-ink-300">$—,———</span>
                : fmtMoney(op?.operating_cash_cents ?? 0, currency, { decimals: 0 })}
            </div>
            <div className="mt-2 text-[13px] text-ink-500">
              {op && (<>
                In: {fmtMoney(op.gross_in_cents, currency, { compact: true })}
                {' · '}Out: {fmtMoney(Math.abs(op.gross_out_cents), currency, { compact: true })}
                {' · '}{op.transactions} transactions
              </>)}
            </div>
            <LearnTip>
              {t('pulse.operating_cash_learn')}
            </LearnTip>
          </div>
          <div className="text-right">
            {op?.delta_pct !== null && op?.delta_pct !== undefined ? (
              <>
                <div className={`pill text-[15px] px-3 py-1 ${op.delta_pct >= 0 ? 'pill-mint' : 'pill-flame'}`}>
                  {op.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(op.delta_pct)}%
                </div>
                <div className="text-[11px] text-ink-500 mt-1">vs prior month ({fmtMoney(op.prior_period_cents, currency, { compact: true })})</div>
              </>
            ) : <div className="text-[11px] text-ink-500">{t('pulse.no_prior_period')}</div>}
          </div>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('pulse.customer_deposit_float')}
            <InfoIcon tip={t('pulse.customer_deposit_float_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {ek?.deposit_float_cents !== null && ek?.deposit_float_cents !== undefined
              ? fmtMoney(ek.deposit_float_cents, currency, { compact: true })
              : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {ek?.active_customers ? `${ek.active_customers} ${t('pulse.active_customers')}` : '—'}
          </div>
          <LearnTip>{t('pulse.customer_deposit_float_learn')}</LearnTip>
        </Card>

        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('pulse.active_labels_today')}
            <InfoIcon tip={t('pulse.active_labels_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {shipKpis.isLoading ? '—' : FMT_INT.format(sk?.ships ?? 0)}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {sk?.velocity_per_day ? `~${sk.velocity_per_day} ${t('pulse.per_day_pace')}` : '—'}
          </div>
          <LearnTip>{t('pulse.active_labels_learn')}</LearnTip>
        </Card>

        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('pulse.days_of_runway')}
            <InfoIcon tip={t('pulse.days_of_runway_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {daysOfRunway !== null ? daysOfRunway : (monthlyOperating > 0 ? '∞' : '—')}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {daysOfRunway !== null ? t('pulse.at_current_burn')
              : monthlyOperating > 0 ? t('pulse.cash_positive')
              : t('pulse.need_monthly_burn')}
          </div>
          <LearnTip>{t('pulse.days_of_runway_learn')}</LearnTip>
        </Card>

        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('pulse.carrier_spend_30d')}
            <InfoIcon tip={t('pulse.carrier_spend_30d_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {ck ? fmtMoney(ck.carrier_30d_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {ck?.carrier_30d_split ? `USPS ${ck.carrier_30d_split.usps_pct}% · DHL ${ck.carrier_30d_split.dhl_pct}%` : '—'}
          </div>
          <LearnTip>{t('pulse.carrier_spend_30d_learn')}</LearnTip>
        </Card>
      </div>

      {/* Status / data freshness */}
      <Card className="p-5 bg-mint-50 border-mint-100">
        <div className="flex items-start gap-3">
          <Icon name="check-circle-2" className="w-5 h-5 text-mint-600 mt-0.5" />
          <div className="text-[13px] text-ink-700 leading-relaxed">
            <b className="text-ink-900">Pulse is live.</b> Numbers above are pulled from <code className="text-[12px] bg-white px-1 rounded">chase_transactions</code>,
            <code className="text-[12px] bg-white px-1 rounded ml-1">byelabel_shipments</code>, and customer-derived computations.
            Latest Chase balance: <b>{ck ? fmtMoney(ck.latest_balance_cents ?? 0, currency, { compact: true }) : '—'}</b>
            {' '}as of <b>{ck?.latest_balance_date ?? '—'}</b>.
          </div>
        </div>
      </Card>

      {/* Action queue */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="zap" className="w-4 h-4 text-honey-600" />
          <div className="text-[15px] font-semibold">{t('pulse.action_queue')}</div>
          <div className="text-[12px] text-ink-500 ml-1">{t('pulse.action_queue_sub')}</div>
        </div>
        {pulseActions.isLoading ? (
          <div className="text-[13px] text-ink-400 py-4">{t('pulse.loading_actions')}</div>
        ) : (pulseActions.data?.actions?.length ?? 0) === 0 ? (
          <div className="flex items-center gap-3 py-4">
            <Icon name="check-circle-2" className="w-5 h-5 text-mint-500" />
            <span className="text-[13px] text-ink-500">{t('pulse.all_clear_actions')}</span>
          </div>
        ) : (
          <div className="divide-y divide-ink-100">
            {pulseActions.data!.actions.map(a => {
              const dotColor = a.priority === 'high'
                ? 'bg-flame-500'
                : a.priority === 'medium'
                  ? 'bg-honey-500'
                  : 'bg-ink-300'
              return (
                <button
                  key={a.type}
                  onClick={() => navigate(a.href)}
                  className="w-full flex items-center gap-4 py-3 text-left hover:bg-ink-50 rounded-lg px-2 -mx-2 transition"
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-ink-800 leading-tight">{a.label}</div>
                    {a.amount_cents !== undefined && (
                      <div className="text-[11.5px] text-ink-500 mt-0.5">
                        {fmtMoney(a.amount_cents, currency)} across {FMT_INT.format(a.count)} records
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.amount_cents === undefined && (
                      <span className="text-[13px] font-semibold text-ink-700 num">{FMT_INT.format(a.count)}</span>
                    )}
                    <Icon name="chevron-right" className="w-4 h-4 text-ink-400" />
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
